import * as React from "react";
import Client from "~/api/client";
import { DeleteReason } from "~/api/types";
import ProductImageModerationForm from "~/components/ProductImageModeration/ProductImageModerationForm";
import {
  IAliExpressPullingImage,
  IProduct
} from "~/graphql/queries/aliexpress/optionPendingModeration";
import ModerationActivityManager from "~/util/registerModerationActivity";

export enum ErrorCode {
  MAIN_IMAGE_ID_MISSING
}

interface IProps {
  product: IProduct;
  images: IAliExpressPullingImage[];
  color: string | null;

  mainImageId: string | null;
  optionId: string;

  onFinish: () => any;
}

interface IState {
  loading: boolean;
  productId: string | null;
  mainImageId: string | null;
  errorCode: ErrorCode | null;
  discardImageIds: string[];
  lastDiscardImageId?: string;
}

export default class ProductImageModerationFormContainer extends React.Component<
  IProps,
  IState
> {
  public static getDerivedStateFromProps(props: IProps, state: IState) {
    // If mainImageId is not set or equal to current state, don't update
    if (props.product.id === state.productId) {
      return null;
    }

    return {
      ...state,
      discardImageIds: [],
      mainImageId:
        props.mainImageId || props.images.length === 1
          ? props.images[0].id
          : null,
      productId: props.product.id
    };
  }

  public readonly state: IState = {
    discardImageIds: [],
    errorCode: null,
    loading: false,
    mainImageId: null,
    productId: null
  };

  public render() {
    const { images, product, color } = this.props;
    const { mainImageId, discardImageIds, errorCode } = this.state;

    return (
      <ProductImageModerationForm
        errorCode={errorCode}
        product={product}
        color={color}
        images={images}
        mainImageId={mainImageId}
        discardImageIds={discardImageIds}
        onImageSelect={this.handleImageSelect}
        onImageDiscard={this.handleImageDiscard}
        onSkip={this.handleSkip}
        onSubmit={this.handleSubmit}
        onDelete={this.handleDelete}
      />
    );
  }

  public handleSubmit = async () => {
    const { optionId, onFinish } = this.props;
    const { mainImageId, discardImageIds, loading } = this.state;

    if (loading) {
      return;
    }

    this.setState({ loading: true });

    if (!mainImageId) {
      this.setState({ errorCode: ErrorCode.MAIN_IMAGE_ID_MISSING });

      return;
    }

    ModerationActivityManager.registerModerationActivity(
      "submit",
      this.props.optionId
    );
    onFinish();
    this.setState({ errorCode: null, loading: false });
    await Client.submitImageTask(optionId, mainImageId, discardImageIds);
  };

  public handleDelete = (reason: DeleteReason) => async () => {
    if (this.state.loading) {
      return;
    }

    this.setState({ loading: true, errorCode: null });

    ModerationActivityManager.registerModerationActivity(
      "delete",
      this.props.optionId
    );
    this.props.onFinish();
    this.setState({ loading: false });
    await Client.deleteImageTask(this.props.product.id, reason);
  };

  public handleSkip = () => {
    ModerationActivityManager.registerModerationActivity(
      "skip",
      this.props.optionId
    );
    this.props.onFinish();
  };

  public handleImageSelect = (imageId: string) =>
    this.setState(state => ({
      discardImageIds: state.discardImageIds.filter(id => id !== imageId),
      mainImageId: state.mainImageId === imageId ? null : imageId
    }));

  public handleImageDiscard = (imageId: string, bulk: boolean = false) => {
    const { lastDiscardImageId } = this.state;

    if (bulk && lastDiscardImageId) {
      this.handleImageDiscard(lastDiscardImageId);

      const { images } = this.props;
      const lastIndex = images.findIndex(
        image => image.id === lastDiscardImageId
      );
      const currentIndex = images.findIndex(image => image.id === imageId);

      const startIndex = lastIndex < currentIndex ? lastIndex : currentIndex;
      const endIndex = lastIndex > currentIndex ? lastIndex : currentIndex;

      images.slice(startIndex, endIndex + 1).map(image => {
        this.handleImageDiscard(image.id);
      });

      return;
    }

    // If it is in the list, take it out, otherwise, put it in the list
    this.setState(state => {
      if (state.discardImageIds.includes(imageId)) {
        return {
          ...state,
          discardImageIds: state.discardImageIds.filter(id => id !== imageId),
          lastDiscardImageId: imageId
        };
      } else {
        return {
          ...state,
          discardImageIds: [...state.discardImageIds, imageId],
          lastDiscardImageId: imageId,
          mainImageId: state.mainImageId === imageId ? null : state.mainImageId
        };
      }
    });
  };
}



// WEBPACK FOOTER //
// ./src/containers/ProductImageModerationFormContainer.tsx